import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faGithub, faAws } from '@fortawesome/free-brands-svg-icons';
import useGithubSshConfigs from '../hooks/useGithubSshConfigs';
import { useGitHubInstallations } from '../hooks/useGitHubInstallations';
import { useAwsConnections } from '../hooks/useAwsConnections';
import { useTenants } from '../hooks/useTenants';
import ResponsiveTable from './ResponsiveTable';
import GlossaryPanel from './GlossaryPanel';
import { useGlossary } from '../hooks/useGlossary';
import '../styles/shared.scss';

const ConnectionsOverview: React.FC = () => {
    const { data: sshConnections = [], isLoading: sshLoading } = useGithubSshConfigs();
    const { data: installations = [], isLoading: appLoading } = useGitHubInstallations();
    const { data: tenants = [] } = useTenants();
    const { isOpen, scrollToItem, openGlossary, closeGlossary } = useGlossary();

    // Get AWS connections for overview
    const { data: awsConnections = [], isLoading: awsLoading } = useAwsConnections();
    const allAwsConnections = awsConnections;

    const getTenantName = (tenantId: string) => {
        const tenant = tenants.find(t => t.id === tenantId);
        return tenant?.name || tenantId;
    };

    if (sshLoading || appLoading || awsLoading) {
        return <div className="loading-message">Loading connections...</div>;
    }

    return (
        <div>
            <div className="page-header">
                <div className="page-title-group">
                    <h2 className="page-title">Connections</h2>
                    <button
                        onClick={() => openGlossary('GitHub Connection')}
                        className="info-button"
                        title="View definition"
                    >
                        Info
                    </button>
                </div>
            </div>

            <div className="section">
                <div className="section-header">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faGithub} className="section-icon" />
                        GitHub
                    </h3>
                    <Link to="/connections/github" className="view-all-link">
                        View All
                    </Link>
                </div>
                
                {sshConnections.length === 0 && installations.length === 0 ? (
                    <p className="empty-message">No GitHub connections configured</p>
                ) : (
                    <ResponsiveTable
                        columns={[
                            {
                                key: 'name',
                                header: 'Name',
                                render: (value, row) => (
                                    <Link
                                        to="/connections/github"
                                        className="table-link"
                                    >
                                        {row.name || (row.repositoryUrl ? row.repositoryUrl.split('/').slice(-2).join('/') : 'GitHub App')}
                                    </Link>
                                )
                            },
                            { 
                                key: 'type', 
                                header: 'Type',
                                render: (value, row) => {
                                    if (row.repositoryUrl && row.accessMethod === 'pat') {
                                        return <span className="connection-type-badge">PAT</span>;
                                    } else if (row.repositoryUrl) {
                                        return <span className="connection-type-badge">SSH</span>;
                                    } else {
                                        return <span className="connection-type-badge">App</span>;
                                    }
                                }
                            },
                            { 
                                key: 'repository', 
                                header: 'Repository/Account',
                                render: (value, row) => {
                                    if (row.repositoryUrl) {
                                        return row.repositoryUrl.split('/').slice(-2).join('/');
                                    }
                                    return row.accountLogin || 'N/A';
                                }
                            },
                            { 
                                key: 'tenantId', 
                                header: 'Tenant',
                                render: (value, row) => {
                                    if (row.repositoryUrl) {
                                        // SSH connections always show tenant name
                                        return getTenantName(row.tenantId);
                                    } else {
                                        // GitHub App installations - show shared tenant or owner tenant
                                        if (row.isShared && row.sharedWithTenant) {
                                            return `${getTenantName(row.sharedWithTenant)} (Shared)`;
                                        }
                                        return getTenantName(row.tenantId || 'Unknown');
                                    }
                                }
                            },
                            {
                                key: 'isActive',
                                header: 'Status',
                                render: (value) => (
                                    <span className={`status-badge ${value ? 'status-badge--active' : 'status-badge--inactive'}`}>
                                        <FontAwesomeIcon icon={value ? faCheck : faTimes} />
                                        {value ? 'Active' : 'Inactive'}
                                    </span>
                                )
                            }
                        ]}
                        data={[...sshConnections, ...installations]}
                    />
                )}
            </div>

            <div className="section">
                <div className="section-header">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faAws} className="section-icon" />
                        AWS
                    </h3>
                    <Link to="/connections/aws" className="view-all-link">
                        View All
                    </Link>
                </div>
                
                {allAwsConnections.length === 0 ? (
                    <p className="empty-message">No AWS connections configured</p>
                ) : (
                    <ResponsiveTable
                        columns={[
                            {
                                key: 'name',
                                header: 'Name',
                                render: (value, row) => (
                                    <Link
                                        to={`/connections/aws/aws-${row.tenantId}-${row.id}`}
                                        className="table-link"
                                    >
                                        {row.name}
                                    </Link>
                                )
                            },
                            { 
                                key: 'accountId', 
                                header: 'AWS Account',
                                render: (value, row) => (
                                    <div>
                                        <div>{row.accountId}</div>
                                        <div className="table-secondary">{row.region}</div>
                                    </div>
                                )
                            },
                            { 
                                key: 'accessMethod', 
                                header: 'Access Method',
                                render: (value) => (
                                    <span className="connection-type-badge">
                                        {value === 'cross-account-role' ? 'Cross-Account Role' : 'Access Keys'}
                                    </span>
                                )
                            },
                            { 
                                key: 'tenantId', 
                                header: 'Tenant',
                                render: (value) => getTenantName(value)
                            },
                            {
                                key: 'isActive',
                                header: 'Status',
                                render: (value) => (
                                    <span className={`status-badge ${value ? 'status-badge--active' : 'status-badge--inactive'}`}>
                                        <FontAwesomeIcon icon={value ? faCheck : faTimes} />
                                        {value ? 'Active' : 'Inactive'}
                                    </span>
                                )
                            }
                        ]}
                        data={allAwsConnections}
                    />
                )}
            </div>

            <GlossaryPanel 
                isOpen={isOpen} 
                onClose={closeGlossary} 
                scrollToItem={scrollToItem} 
            />
        </div>
    );
};

export default ConnectionsOverview;